import FetchClient, { type ApiResponse } from "./FetchClient";
import { memoryCache } from "./MemoryCache";

export interface FavoriteProduct {
  upc: string;
  name: string;
  image: string;
  category: string;
  adjustedCategory?: string;
  msrp: number;
  onlinePrice: number;
  storePrice: number[];
  added: number;
  lastUpdated: number;
  url: string;
  sku: string;
  inStock: boolean;
  storeId?: string;
  // Snapshot values captured when the favorite was saved.
  savedPrice?: number;
  savedOnlinePrice?: number;
  savedStock?: number;
  // When the snapshot was last refreshed by a nearby check.
  lastChecked?: number;
}

export interface FavoritesResponse {
  favorites: FavoriteProduct[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface AddFavoriteRequest {
  upc: string;
  name?: string;
  image?: string;
  category?: string;
  retailer?: string;
  storeId?: string;
  // Snapshot of the product at save time (price/stock), so the favorite can
  // show the discount it was saved at.
  onlinePrice?: number;
  storePrice?: number;
  stock?: number;
}

// Full product data for walmart_online favorites
export interface AddWalmartOnlineFavoriteRequest {
  upc: string;
  title: string;
  image: string;
  category: string;
  adjustedCategory?: string;
  msrp: number;
  currentPrice: number;
  currentPrices: number[];
  url: string;
  sku: string;
  lastUpdated: number;
  lastUpdates: number[];
  discountPercentage: number;
}

export interface QuickSaveFavoriteRequest {
  upc: string;
  name?: string;
  image?: string;
  retailer?: string;
  storeId?: string;
}

export interface QuickSaveFavoriteResponse {
  message: string;
  upc: string;
  fast: boolean;
  alreadyExists?: boolean;
}

export interface FavoriteUPCsResponse {
  upcs: string[];
}

/**
 * Get user's favorite products with pagination
 */
export const getFavorites = async (
  pageSize: number = 20,
  cursor?: string,
  basic: boolean = false
): Promise<FavoritesResponse> => {
  return getFavoritesWithRetailer(pageSize, cursor, "walmart", basic);
};

/**
 * Get user's favorite products with pagination for a specific retailer
 */
export const getFavoritesWithRetailer = async (
  pageSize: number = 20,
  cursor?: string,
  retailer: string = "walmart",
  basic: boolean = false
): Promise<FavoritesResponse> => {
  const params = new URLSearchParams();
  params.append("pageSize", pageSize.toString());
  if (cursor) {
    params.append("cursor", cursor);
  }
  if (basic) {
    params.append("basic", "true");
  }

  const url = `/${retailer}/favorites?${params.toString()}`;
  const response: ApiResponse<FavoritesResponse> = await FetchClient.get(url);

  return response.data;
};

/**
 * Add a product to favorites with complete product data
 */
export const addFavorite = async (
  productData: AddFavoriteRequest,
  basic: boolean = false
): Promise<FavoritesResponse> => {
  return addFavoriteWithRetailer(productData, "walmart", basic);
};

/**
 * Add a product to favorites with complete product data for a specific retailer
 */
export const addFavoriteWithRetailer = async (
  productData: AddFavoriteRequest,
  retailer: string = "walmart",
  basic: boolean = false
): Promise<FavoritesResponse> => {
  const requestData = { ...productData, retailer };
  const params = new URLSearchParams();
  if (basic) {
    params.append("basic", "true");
  }

  const url = `/${retailer}/favorites${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const response: ApiResponse<FavoritesResponse> = await FetchClient.post(
    url,
    requestData
  );

  // Update cache to reflect that this product is now a favorite
  const cacheKey = `favorite:${productData.upc}:${retailer}`;
  memoryCache.set(cacheKey, true, 5 * 60 * 1000);

  // Invalidate both favorites list cache and lightweight UPC cache
  memoryCache.deletePattern(/^favorites:/);
  memoryCache.delete(`favorite-upcs:${retailer}`);

  return response.data;
};

/**
 * Add a walmart_online favorite with full product data
 */
export const addWalmartOnlineFavorite = async (
  productData: AddWalmartOnlineFavoriteRequest
): Promise<FavoritesResponse> => {
  const url = `/walmart_online/favorites`;
  const response: ApiResponse<FavoritesResponse> = await FetchClient.post(
    url,
    productData
  );

  const cacheKey = `favorite:${productData.upc}:walmart_online`;
  memoryCache.set(cacheKey, true, 5 * 60 * 1000);
  memoryCache.deletePattern(/^favorites:/);
  memoryCache.delete(`favorite-upcs:walmart_online`);

  return response.data;
};

/**
 * Quickly save a product to favorites with minimal data (optimized for speed)
 */
export const quickSaveFavorite = async (
  upc: string,
  name?: string,
  image?: string,
  storeId?: string
): Promise<QuickSaveFavoriteResponse> => {
  return quickSaveFavoriteWithRetailer(upc, "walmart", name, image, storeId);
};

/**
 * Quickly save a product to favorites with minimal data for a specific retailer (optimized for speed)
 */
export const quickSaveFavoriteWithRetailer = async (
  upc: string,
  retailer: string = "walmart",
  name?: string,
  image?: string,
  storeId?: string
): Promise<QuickSaveFavoriteResponse> => {
  const requestData: QuickSaveFavoriteRequest = {
    upc,
    retailer,
    ...(name && { name }),
    ...(image && { image }),
    ...(storeId && { storeId }),
  };

  const response: ApiResponse<QuickSaveFavoriteResponse> =
    await FetchClient.post(`/${retailer}/favorites/quick-save`, requestData);

  // Update cache to reflect that this product is now a favorite (unless it already existed)
  if (!response.data.alreadyExists) {
    const cacheKey = `favorite:${upc}:${retailer}`;
    memoryCache.set(cacheKey, true, 5 * 60 * 1000);

    // Invalidate both favorites list cache and lightweight UPC cache
    memoryCache.deletePattern(/^favorites:/);
    memoryCache.delete(`favorite-upcs:${retailer}`);
  }

  return response.data;
};

/**
 * Remove a product from favorites
 */
export const removeFavorite = async (
  upc: string,
  basic: boolean = false
): Promise<FavoritesResponse> => {
  return removeFavoriteWithRetailer(upc, "walmart", basic);
};

/**
 * Remove a product from favorites for a specific retailer
 */
export const removeFavoriteWithRetailer = async (
  upc: string,
  retailer: string = "walmart",
  basic: boolean = false
): Promise<FavoritesResponse> => {
  const params = new URLSearchParams();
  if (basic) {
    params.append("basic", "true");
  }

  const url = `/${retailer}/favorites/${encodeURIComponent(upc)}${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const response: ApiResponse<FavoritesResponse> = await FetchClient.delete(
    url
  );

  // Update cache to reflect that this product is no longer a favorite
  const cacheKey = `favorite:${upc}:${retailer}`;
  memoryCache.set(cacheKey, false, 5 * 60 * 1000);

  // Invalidate both favorites list cache and lightweight UPC cache
  memoryCache.deletePattern(/^favorites:/);
  memoryCache.delete(`favorite-upcs:${retailer}`);

  return response.data;
};

/**
 * Refresh a favorite's price/stock snapshot after a nearby check
 */
export const updateFavoriteSnapshot = async (
  upc: string,
  retailer: string,
  snapshot: { storePrice: number; onlinePrice?: number; stock?: number }
): Promise<void> => {
  await FetchClient.patch(`/${retailer}/favorites/${encodeURIComponent(upc)}`, snapshot);
};

/**
 * Get user's favorite UPCs only (lightweight) for a specific retailer
 */
export const getFavoriteUPCsWithRetailer = async (
  retailer: string = "walmart"
): Promise<string[]> => {
  const cacheKey = `favorite-upcs:${retailer}`;

  // Check cache first
  const cached = memoryCache.get<string[]>(cacheKey);
  if (cached !== null) {
    return cached;
  }

  try {
    const response: ApiResponse<FavoriteUPCsResponse> = await FetchClient.get(
      `/${retailer}/favorites/upcs`
    );

    // Cache the result for 5 minutes
    memoryCache.set(cacheKey, response.data.upcs, 5 * 60 * 1000);
    return response.data.upcs;
  } catch (error) {
    console.error("Error getting favorite UPCs:", error);
    // Cache empty result for 1 minute to avoid repeated failed requests
    memoryCache.set(cacheKey, [], 60 * 1000);
    return [];
  }
};
